import { Flex, Image, Tabs, Text } from '@chakra-ui/react';
import SearchInput from '@components/common/SearchInput';
import { allCommodities, PROCUREMENT_CATALOG } from 'helpers/fakeData';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react-lite';
import pluralize from 'pluralize';
import { useCallback, useMemo, useState } from 'react';
import { LuPackage, LuPlus } from 'react-icons/lu';
import { SearchableEntity } from 'stores/pageStore';
import { usePageStore } from 'stores/rootStore';

const getLabel = (value: SearchableEntity) => {
  switch (value) {
    case SearchableEntity.Commodity:
      return 'Commodity';
    case SearchableEntity.Contract:
      return 'Contract';
  }
};

const TAB_ITEMS = Object.values(SearchableEntity).map((value) => ({
  label: getLabel(value),
  value,
}));

interface TabContentProps {
  value: SearchableEntity;
  query: string;
  closeModal: () => void;
}
const TabContent = ({ value, query, closeModal }: TabContentProps) => {
  switch (value) {
    case SearchableEntity.Commodity:
      return <CommodityTabContent query={query} closeModal={closeModal} />;
    case SearchableEntity.Contract:
      return <ContractTabContent query={query} closeModal={closeModal} />;
  }
};

const commodityById = new Map(allCommodities.map((commodity) => [commodity.id, commodity]));

// Each category reveals this many items at a time so a large category can't
// dominate; "Show more" pages the next batch.
const CATEGORY_PAGE_SIZE = 5;

// Procurement targets either a market commodity or a broader catalog item, and
// also allows a freeform name. Results stay grouped by category and searchable.
const CommodityTabContent = observer(({ query, closeModal }: Omit<TabContentProps, 'value'>) => {
  const pageStore = usePageStore();
  const trimmedQuery = query.trim();
  // Paging is scoped to the query it was opened under; a different query resets
  // counts during render (no post-paint flash, no extra render).
  const [paging, setPaging] = useState<{ query: string; counts: Record<string, number> }>({
    query: trimmedQuery,
    counts: {},
  });
  const visibleByCategory = paging.query === trimmedQuery ? paging.counts : {};

  const onShowMore = useCallback(
    (category: string) => () => {
      setPaging((prev) => {
        const counts = prev.query === trimmedQuery ? prev.counts : {};
        return {
          query: trimmedQuery,
          counts: { ...counts, [category]: (counts[category] ?? CATEGORY_PAGE_SIZE) + CATEGORY_PAGE_SIZE },
        };
      });
    },
    [trimmedQuery],
  );

  const groups = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    return PROCUREMENT_CATALOG.map((group) => ({
      category: group.category,
      items: group.items.filter((item) => {
        if (q === '') {
          return true;
        }
        const ticker = commodityById.get(item.id)?.ticker ?? '';
        return item.name.toLowerCase().includes(q) || ticker.toLowerCase().includes(q);
      }),
    })).filter((group) => group.items.length > 0);
  }, [trimmedQuery]);

  const hasExactMatch = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    return PROCUREMENT_CATALOG.some((group) =>
      group.items.some((item) => item.name.toLowerCase() === q),
    );
  }, [trimmedQuery]);

  const onSelect = useCallback(
    (id: string, label: string) => () => {
      pageStore.setSearchNavigationState({
        selectedItems: [{ entity: SearchableEntity.Commodity, id, label }],
      });
      closeModal();
    },
    [pageStore, closeModal],
  );

  return (
    <Flex direction="column" gap={3} w="full">
      {trimmedQuery !== '' && !hasExactMatch && (
        <Flex
          direction="row"
          gap={2}
          alignItems="center"
          cursor="pointer"
          _hover={{ bg: 'surface.accent' }}
          p={2}
          borderRadius="md"
          color="selection.600"
          onClick={onSelect(`freeform:${trimmedQuery}`, trimmedQuery)}
        >
          <LuPlus size={18} />
          <Text lineHeight={1} fontWeight="medium">
            Use “{trimmedQuery}”
          </Text>
        </Flex>
      )}
      {groups.map((group) => {
        const visible = visibleByCategory[group.category] ?? CATEGORY_PAGE_SIZE;
        const remaining = group.items.length - visible;
        return (
          <Flex key={group.category} direction="column" gap={1}>
            <Text fontSize="xs" color="gray.500" px={2} fontWeight="medium">
              {group.category}
            </Text>
            {group.items.slice(0, visible).map((item) => {
              const commodity = commodityById.get(item.id);
              return (
                <Flex
                  key={item.id}
                  direction="row"
                  gap={2}
                  alignItems="center"
                  cursor="pointer"
                  _hover={{ bg: 'surface.accent' }}
                  p={2}
                  borderRadius="md"
                  onClick={onSelect(item.id, item.name)}
                >
                  {commodity != null ? (
                    <Image src={commodity.image} alt={item.name} boxSize="32px" />
                  ) : (
                    <Flex
                      boxSize="32px"
                      bg="surface.muted"
                      borderRadius="md"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <LuPackage size={16} />
                    </Flex>
                  )}
                  <Flex direction="column" gap={1}>
                    <Text lineHeight={1}>{item.name}</Text>
                    {commodity != null && (
                      <Text lineHeight={1} color="gray.500">
                        {commodity.ticker}
                      </Text>
                    )}
                  </Flex>
                </Flex>
              );
            })}
            {remaining > 0 && (
              <Text
                fontSize="xs"
                fontWeight="medium"
                color="selection.600"
                cursor="pointer"
                px={2}
                py={1}
                _hover={{ textDecoration: 'underline' }}
                onClick={onShowMore(group.category)}
              >
                Show {Math.min(remaining, CATEGORY_PAGE_SIZE)} more
              </Text>
            )}
          </Flex>
        );
      })}
      {groups.length === 0 && trimmedQuery === '' && (
        <Text fontSize="sm" color="gray.500" px={2}>
          No items
        </Text>
      )}
    </Flex>
  );
});

const ContractTabContent = ({ query }: Omit<TabContentProps, 'value'>) => {
  return <div>TODO: ContractTabContent</div>;
};

const MAX_SUMMARY_ITEMS = 3;
const getPlaceholderText = (searchableEntities: SearchableEntity[]) => {
  if (searchableEntities.length === 0) {
    return 'Search commodities, contracts,...';
  }
  // The commodity picker spans the full procurement catalog and accepts freeform
  // entries, so the placeholder should invite both.
  if (searchableEntities.length === 1 && searchableEntities[0] === SearchableEntity.Commodity) {
    return 'Search items or type your own…';
  }
  const summaryItems = searchableEntities
    .slice(0, MAX_SUMMARY_ITEMS)
    .map((entity) => pluralize(getLabel(entity), 2));
  const baseText = `Search ${summaryItems.join(', ')}`;
  if (searchableEntities.length > MAX_SUMMARY_ITEMS) {
    return capitalize(`${baseText}, ...`.toLowerCase());
  }
  return capitalize(baseText.toLowerCase());
};

const HEIGHT = '500px';
interface SearchContentsProps {
  closeModal: () => void;
}
const SearchContents = ({ closeModal }: SearchContentsProps) => {
  const pageStore = usePageStore();
  const [query, setQuery] = useState(pageStore.initialSearchQuery() ?? '');
  const searchableEntities = pageStore.searchableEntities();

  const tabsToRender = useMemo(() => {
    if (searchableEntities == null) {
      return TAB_ITEMS;
    }
    return TAB_ITEMS.filter((item) => searchableEntities.includes(item.value));
  }, [searchableEntities]);
  const placeholderText = getPlaceholderText(tabsToRender.map((item) => item.value));

  return (
    <Flex direction="column" gap={2}>
      <SearchInput query={query} setQuery={setQuery} placeholder={placeholderText} />
      {tabsToRender.length > 1 ? (
        <Tabs.Root defaultValue={tabsToRender[0].value}>
          <Tabs.List>
            {tabsToRender.map((item) => (
              <Tabs.Trigger key={item.value} value={item.value}>
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
          {tabsToRender.map((item) => (
            <Tabs.Content
              p={0}
              pt={1}
              key={item.value}
              value={item.value}
              height={HEIGHT}
              overflowY="auto"
            >
              <TabContent value={item.value} query={query} closeModal={closeModal} />
            </Tabs.Content>
          ))}
        </Tabs.Root>
      ) : (
        <Flex direction="column" height={HEIGHT} overflowY="auto" p={0} w="full" gap={1}>
          <TabContent value={tabsToRender[0].value} query={query} closeModal={closeModal} />
        </Flex>
      )}
    </Flex>
  );
};

export default observer(SearchContents);
